/**
 * Inverse of `archimate-emitter.ts`: parses the PlantUML text the emitter
 * produces back into a typed ParseResult. M2.6 uses this to drive
 * bidirectional sync — edits in the text pane are parsed and diffed against
 * the live workspace store.
 *
 * The grammar is intentionally narrow: only the calls we ourselves emit
 * (`<TypeProc>(alias, "label")`, `<RelProc>(alias, alias[, "label"])`),
 * plus skippable lines (`@startuml`, `@enduml`, `!include …`, `title …`,
 * blank lines, `'` comments). Any other non-blank line becomes a
 * ParseError so the user sees what tripped the parser.
 *
 * Aliases follow the emitter's `sanitizeId()` convention: `n_<uuid-with-
 * underscores>`. The reverse helper `aliasToConceptId()` undoes that, but
 * the parser also accepts arbitrary identifiers — these represent concepts
 * the user is creating fresh in the text and will get a freshly minted UUID
 * when the diff is applied.
 */
import { RELATION_PROCEDURE, TYPE_PROCEDURE } from "./archimate-emitter";

export interface ParsedConcept {
  /** Identifier as it appears in the text (e.g. `n_abc_def_…` or a
   *  user-typed shortname like `customer`). */
  alias: string;
  typeId: string;
  label: string;
  /** 1-based line number — used to anchor parse errors. */
  line: number;
  /** Alias of the enclosing Grouping when this concept was parsed inside
   *  a `Grouping_Group(g, "…") { … }` brace-block. Drives visual nesting
   *  in the canvas via `viewState.containerId`. Nested groupings stack:
   *  the inner grouping's `containerAlias` is the outer one. */
  containerAlias?: string;
}

export interface ParsedRelation {
  typeId: string;
  /** Source alias as it should be matched in the workspace store. The
   *  emitter swaps source/target for Composition + Aggregation; the parser
   *  un-swaps so callers always see the in-app convention
   *  (source = part, target = whole for those two). */
  sourceAlias: string;
  targetAlias: string;
  label: string;
  line: number;
  /** Optional handle ids parsed from a trailing
   *  `' @handles source=<dir> target=<dir>` directive. Values are
   *  normalised to `top|right|bottom|left` (NL aliases are accepted).
   *  Both directions in the directive refer to the in-app source/target,
   *  not the PlantUML positional args, so applier code can pass them
   *  straight to `setRelationHandlesOnCanvas`. */
  sourceHandle?: string;
  targetHandle?: string;
}

export interface ParseError {
  line: number;
  message: string;
}

export interface ParseResult {
  concepts: ParsedConcept[];
  relations: ParsedRelation[];
  errors: ParseError[];
}

// Same set the emitter uses to swap source/target for PlantUML stdlib's
// `Rel_Composition(whole, part)` convention. The parser un-swaps.
const SWAP_FOR_PLANTUML = new Set(["archimate.Composition", "archimate.Aggregation"]);

// Build inverse maps once. Multiple typeIds could in theory map to the same
// procedure name, but in our packs the mapping is 1-1.
const PROCEDURE_TO_TYPE = invertProcedureMap(TYPE_PROCEDURE);
const PROCEDURE_TO_RELATION = invertProcedureMap(RELATION_PROCEDURE);
// Legacy / alternate procedure names — parser-only aliases so older
// stored PlantUML and PlantUML produced by other tools still round-
// trip into the right typeId. The emitter always writes the canonical
// stdlib name.
const PROCEDURE_ALIASES: Record<string, string> = {
  // Plantuml-stdlib heeft `Grouping($alias, $label)` als de canonical
  // procedure-naam — dat is wat de emitter ook outputs. Twee historische
  // varianten worden ook geaccepteerd zodat content uit andere tools
  // blijft round-trippen:
  //   - `Other_Grouping` — oude fork van Archimate-PlantUML
  //   - `Grouping_Group` — Archi-exports en Claude-output (niet in stdlib
  //     maar wel veelvoorkomend op het web)
  Other_Grouping: "archimate.Grouping",
  Grouping_Group: "archimate.Grouping",
};
for (const [alias, typeId] of Object.entries(PROCEDURE_ALIASES)) {
  if (!PROCEDURE_TO_TYPE[alias]) PROCEDURE_TO_TYPE[alias] = typeId;
}

function invertProcedureMap(map: Record<string, string>): Record<string, string> {
  // Sommige procedures hebben meerdere typeId-bronnen — bv.
  // `Application_Component` is de emit-doelnaam voor zowel
  // `archimate.ApplicationComponent` als de C4-extensies
  // `c4.SoftwareSystemInstance` / `c4.ContainerInstance`. Voor de
  // parser-richting moet de canonical archimate-* typeId winnen,
  // anders zou een gewone `Application_Component(…)`-paste plotseling
  // als c4-extension geparseerd worden. Eerste-wint = archimate-eerst
  // (de mappings staan in TYPE_PROCEDURE op archimate-volgorde, met
  // c4-mappings onderaan).
  const out: Record<string, string> = {};
  for (const [typeId, proc] of Object.entries(map)) {
    if (out[proc]) continue;
    out[proc] = typeId;
  }
  return out;
}

const CALL_RE = /^\s*([A-Za-z][\w]*)\s*\(([\s\S]*)\)\s*$/;

export function parseArchiMatePlantUML(text: string): ParseResult {
  const concepts: ParsedConcept[] = [];
  const relations: ParsedRelation[] = [];
  const errors: ParseError[] = [];

  const lines = text.split(/\r?\n/);
  // Multi-line `note … end note` blocks are skipped wholesale — they
  // don't carry semantic content for the model and we'd otherwise emit
  // "Onbegrepen regel" for every wrapped text-line inside the block.
  let inNoteBlock = false;
  // Stack of currently-open Grouping aliases. `Grouping_Group(g, "…") {`
  // pushes; the matching `}` pops. Concepts parsed while the stack is
  // non-empty get a synthetic `archimate.Aggregation` relation back to
  // the top-of-stack grouping (whole=grouping, part=child) so brace-block
  // membership becomes a real edge in the model — applyParseResult diffs
  // these like any explicitly-typed relation.
  const groupingStack: string[] = [];
  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i] ?? "";
    const lineNum = i + 1;
    // Strip any trailing `'` comment (quote-aware so a `'` inside a label
    // doesn't terminate the call). We keep the comment text around so a
    // `@handles` directive can still influence the parsed relation.
    const split = splitLineComment(raw);
    let line = split.code.trim();
    const trailingComment = split.comment;
    if (line === "") continue;
    // Inside a `note … end note` block: silently swallow every line
    // until we hit `end note`. The block itself is purely decorative.
    if (inNoteBlock) {
      if (/^end\s+note\b/i.test(line)) inNoteBlock = false;
      continue;
    }
    if (line.startsWith("@startuml") || line.startsWith("@enduml")) continue;
    if (line.startsWith("!include") || line.startsWith("!define")) continue;
    // Layout / routing-directives uit de emitter-preamble. Niet
    // structureel — alleen render-hints voor PlantUML zelf.
    if (line.startsWith("!pragma") || line.startsWith("skinparam ")) continue;
    if (line.startsWith("title ")) continue;
    // PlantUML section separators like `===== Notes =====` — purely
    // visual structure in the source; no meaning for our model.
    if (/^=+.*=+$/.test(line)) continue;
    // Note blocks. PlantUML supports both forms:
    //   note right of foo : single-line text                ← skip the line
    //   note left of foo                                    ← skip until
    //     wrapped multiline body                              `end note`
    //   end note
    //   note as N1 / note "text" as N1                      ← standalone
    if (/^note\b/i.test(line)) {
      // Single-line variant has a `:` after the anchor; otherwise it's
      // a multi-line block opener.
      if (line.includes(":")) continue;
      inNoteBlock = true;
      continue;
    }
    // The ArchiMate-PlantUML stdlib (and Claude / Archi exports) wrap
    // grouped contents in a `{ … }` block, e.g.
    //   Grouping_Group(g, "…") {
    //     Business_Actor(a, "…")
    //   }
    // A trailing `{` on a Grouping call opens a membership scope: every
    // concept inside it picks up an aggregation back to that grouping
    // (handled below where the concept is parsed). The closing `}` pops
    // that scope. A bare `{` on its own line is just decoration.
    if (line === "}") {
      groupingStack.pop();
      continue;
    }
    if (line === "{") continue;
    // Hidden-waypoint statements uit de emitter (zie
    // plantuml-aanhechting.md "Stap B"). Per waypoint emit'en we een
    // anonieme `rectangle wp_<relId>_<idx>` + `hide wp_<…>`-statement
    // zodat ELK een knik in de lijn maakt op de canvas-waypoint-positie
    // zonder visuele node te tonen. Bij parse: skippen — de waypoint-
    // data woont in viewState, niet in de tekst-roundtrip.
    if (/^rectangle\s+wp_[A-Za-z0-9_]+/.test(line)) continue;
    if (/^hide\s+wp_[A-Za-z0-9_]+/.test(line)) continue;
    // Plain PlantUML pijl-syntax tussen waypoint-aliases (wp_X --> wp_Y
    // of A --> wp_X). Niet via stdlib-procedure → CALL_RE matched 'm
    // niet, en de Onbegrepen-regel-error zou ze als parse-fouten
    // markeren. Skip ze hier in plaats.
    if (/\bwp_[A-Za-z0-9_]+\b/.test(line) && /-+>|<-+/.test(line)) continue;
    let opensGroupingBlock = false;
    if (line.endsWith("{")) {
      opensGroupingBlock = true;
      line = line.slice(0, -1).trimEnd();
    }

    const m = line.match(CALL_RE);
    if (!m) {
      errors.push({ line: lineNum, message: `Onbegrepen regel: ${line}` });
      continue;
    }
    const procName = m[1] as string;
    const rawArgs = m[2] as string;

    const args = splitArgs(rawArgs);
    if (args === null) {
      errors.push({ line: lineNum, message: "Onafgesloten string in argumenten" });
      continue;
    }

    // Strip directional suffix (`_Up` / `_Down` / `_Left` / `_Right`) van
    // de procedure-naam vóór lookup. De emitter voegt die suffix toe op
    // basis van viewState.sourceHandle om PlantUML/ELK richting-hints te
    // geven; voor de parser is alleen de basis-procedure relevant. Handle-
    // info wordt teruggehaald via de `' @handles`-comment-conventie die
    // de emitter ook schrijft.
    const procBase = procName.replace(/_(Up|Down|Left|Right)$/, "");
    const conceptType = PROCEDURE_TO_TYPE[procName] ?? PROCEDURE_TO_TYPE[procBase];
    const relationType = PROCEDURE_TO_RELATION[procName] ?? PROCEDURE_TO_RELATION[procBase];

    if (conceptType) {
      // Concept: expect (alias, "label")
      if (args.length !== 2) {
        errors.push({
          line: lineNum,
          message: `${procName} verwacht 2 argumenten (alias, "label"), kreeg er ${args.length}`,
        });
        continue;
      }
      const alias = args[0] as string;
      const labelToken = args[1] as string;
      if (!isIdentifier(alias)) {
        errors.push({ line: lineNum, message: `Ongeldige alias: ${alias}` });
        continue;
      }
      const label = unquote(labelToken);
      if (label === null) {
        errors.push({ line: lineNum, message: "Label moet een quoted string zijn" });
        continue;
      }
      const concept: ParsedConcept = { alias, typeId: conceptType, label, line: lineNum };
      // If we're inside one or more open grouping-blocks, record the
      // innermost grouping as this concept's visual container. The
      // applier turns this into `viewState.containerId` so the child
      // renders inside the Grouping shape on the canvas — same data
      // model as the manual drag-drop "inschuiven" gesture.
      if (groupingStack.length > 0) {
        concept.containerAlias = groupingStack[groupingStack.length - 1] as string;
      }
      concepts.push(concept);
      // A `Grouping_Group(g, "…") {` opens a new containment scope for
      // every concept that follows until the matching `}`. Nested
      // groupings nest naturally on the stack: the inner grouping is
      // itself a child of the outer one (the `containerAlias` block above
      // already captured that, since the inner-grouping line was parsed
      // while only the outer one was on the stack).
      if (opensGroupingBlock && conceptType === "archimate.Grouping") {
        groupingStack.push(alias);
      }
      continue;
    }

    if (relationType) {
      // Relation: expect (alias, alias) or (alias, alias, "label")
      if (args.length !== 2 && args.length !== 3) {
        errors.push({
          line: lineNum,
          message: `${procName} verwacht 2 of 3 argumenten, kreeg er ${args.length}`,
        });
        continue;
      }
      const a = args[0] as string;
      const b = args[1] as string;
      if (!isIdentifier(a) || !isIdentifier(b)) {
        errors.push({ line: lineNum, message: `Ongeldige alias in ${procName}(…)` });
        continue;
      }
      let label = "";
      if (args.length === 3) {
        const lbl = unquote(args[2] as string);
        if (lbl === null) {
          errors.push({ line: lineNum, message: "Label moet een quoted string zijn" });
          continue;
        }
        label = lbl;
      }
      // Un-swap for Composition/Aggregation so the rest of the app always
      // sees source=part, target=whole.
      const swap = SWAP_FOR_PLANTUML.has(relationType);
      const sourceAlias = swap ? b : a;
      const targetAlias = swap ? a : b;
      const handles = parseHandlesDirective(trailingComment);
      const rel: ParsedRelation = {
        typeId: relationType,
        sourceAlias,
        targetAlias,
        label,
        line: lineNum,
      };
      if (handles.source) rel.sourceHandle = handles.source;
      if (handles.target) rel.targetHandle = handles.target;
      relations.push(rel);
      continue;
    }

    errors.push({ line: lineNum, message: `Onbekende procedure: ${procName}` });
  }

  return { concepts, relations, errors };
}

/**
 * Reverses `sanitizeId()` from the emitter: strip the `n_` prefix and turn
 * underscores back into hyphens. Returns `null` if the alias doesn't match
 * the prefix, signalling a user-typed fresh alias that needs a new UUID.
 */
export function aliasToConceptId(alias: string): string | null {
  if (!alias.startsWith("n_")) return null;
  return alias.slice(2).replace(/_/g, "-");
}

// ---- Helpers --------------------------------------------------------------

function isIdentifier(s: string): boolean {
  return /^[A-Za-z][\w]*$/.test(s);
}

/**
 * Splits a comma-separated argument list, treating quoted strings as
 * atomic. Returns `null` on an unterminated string.
 */
function splitArgs(s: string): string[] | null {
  const out: string[] = [];
  let cur = "";
  let inStr = false;
  let escaped = false;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (inStr) {
      if (escaped) {
        cur += ch;
        escaped = false;
      } else if (ch === "\\") {
        cur += ch;
        escaped = true;
      } else if (ch === '"') {
        cur += ch;
        inStr = false;
      } else {
        cur += ch;
      }
      continue;
    }
    if (ch === '"') {
      cur += ch;
      inStr = true;
    } else if (ch === ",") {
      out.push(cur.trim());
      cur = "";
    } else {
      cur += ch;
    }
  }
  if (inStr) return null;
  const last = cur.trim();
  if (last) out.push(last);
  return out;
}

function unquote(s: string): string | null {
  if (s.length < 2 || !s.startsWith('"') || !s.endsWith('"')) return null;
  return s.slice(1, -1).replace(/\\"/g, '"').replace(/\\\\/g, "\\");
}

/**
 * Splits a line into (code, comment) at the first unquoted `'`. Quoted
 * strings are respected so a `'` inside a label is preserved.
 */
function splitLineComment(line: string): { code: string; comment: string | null } {
  let inStr = false;
  let escaped = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (escaped) {
      escaped = false;
      continue;
    }
    if (inStr) {
      if (ch === "\\") escaped = true;
      else if (ch === '"') inStr = false;
      continue;
    }
    if (ch === '"') {
      inStr = true;
    } else if (ch === "'") {
      return { code: line.slice(0, i), comment: line.slice(i + 1) };
    }
  }
  return { code: line, comment: null };
}

/**
 * Parses a `@handles source=<dir> target=<dir>` directive out of a trailing
 * comment. Either field is optional.
 */
function parseHandlesDirective(comment: string | null): { source?: string; target?: string } {
  if (!comment) return {};
  const m = /@handles\b([^@]+)/.exec(comment);
  if (!m) return {};
  const out: { source?: string; target?: string } = {};
  const body = m[1] ?? "";
  for (const part of body.trim().split(/\s+/)) {
    const eq = part.indexOf("=");
    if (eq === -1) continue;
    const key = part.slice(0, eq).toLowerCase();
    const val = normaliseHandle(part.slice(eq + 1));
    if (!val) continue;
    if (key === "source") out.source = val;
    else if (key === "target") out.target = val;
  }
  return out;
}

/**
 * Normalises a handle direction. Accepts canonical English handle ids
 * (`top`, `right`, `bottom`, `left`), Dutch directional names
 * (`noord`, `oost`, `zuid`, `west`), and single-letter shortcuts in
 * either language. Returns `null` for unrecognised values.
 */
function normaliseHandle(raw: string): string | null {
  const k = raw.trim().toLowerCase();
  switch (k) {
    case "top":
    case "noord":
    case "n":
      return "top";
    case "right":
    case "oost":
    case "east":
    case "e":
    case "o":
      return "right";
    case "bottom":
    case "zuid":
    case "south":
    case "s":
    case "z":
      return "bottom";
    case "left":
    case "west":
    case "w":
      return "left";
    default:
      return null;
  }
}
